// The matcher behind the spotlight.
//
// Everything here runs synchronously on every keystroke, so it is deliberately
// boring: a single pass over a few hundred entries whose lowercase title,
// initials and haystack were precomputed at catalog-build time. No regex
// compilation per keystroke, no allocation per candidate that doesn't match,
// no debounce — the whole search is well under a millisecond, which is what
// makes typing feel instant.

// Score floors, highest intent first.
const SCORE_EXACT = 1200
const SCORE_TITLE_PREFIX = 1000
const SCORE_TITLE_WORD = 860
const SCORE_INITIALS = 780
const SCORE_TITLE_SUBSTRING = 700
const SCORE_ALL_TOKENS = 560
const SCORE_HAYSTACK = 460
const SCORE_TITLE_FUZZY = 300
const SCORE_HAYSTACK_FUZZY = 120

// Subsequence matching only earns its keep on queries long enough to be
// intentional — "cod" would otherwise scatter-match "Info Center".
const MIN_TITLE_FUZZY_LENGTH = 3
const MIN_HAYSTACK_FUZZY_LENGTH = 4

/**
 * Subsequence match: are all of `query`'s characters present in `text`, in
 * order? Returns a 0–100 quality bonus (0 = no match) that rewards matches
 * that are contiguous and start on word boundaries, so "ordfrm" ranks
 * "Order Form" above a coincidental scatter match.
 */
function fuzzyBonus(text, query) {
  const textLength = text.length
  const queryLength = query.length

  if (queryLength > textLength) {
    return 0
  }

  let textIndex = 0
  let streak = 0
  let bonus = 0

  for (let queryIndex = 0; queryIndex < queryLength; queryIndex++) {
    const char = query[queryIndex]
    const found = text.indexOf(char, textIndex)

    if (found === -1) {
      return 0
    }

    if (found === textIndex && queryIndex > 0) {
      streak += 1
      bonus += 4 + streak
    } else {
      streak = 0
      if (found === 0 || text[found - 1] === ' ' || text[found - 1] === '/') {
        bonus += 5
      }
    }

    textIndex = found + 1
  }

  // Tight matches (few skipped characters) beat sprawling ones.
  const density = Math.round((queryLength / textIndex) * 30)
  return Math.min(bonus + density, 100)
}

function everyToken(haystack, tokens) {
  for (let i = 0; i < tokens.length; i++) {
    if (haystack.indexOf(tokens[i]) === -1) {
      return false
    }
  }
  return true
}

/**
 * Returns the match strength before any personalisation, so the caller can
 * tell a real hit from a fuzzy guess. 0 means no match.
 */
function baseScore(entry, query, tokens) {
  const title = entry.lowerTitle
  let score = 0

  if (title === query) {
    score = SCORE_EXACT
  } else {
    const index = title.indexOf(query)

    if (index === 0) {
      score = SCORE_TITLE_PREFIX
    } else if (index > 0) {
      const previous = title[index - 1]
      score = (previous === ' ' || previous === '-' || previous === '/') ? SCORE_TITLE_WORD : SCORE_TITLE_SUBSTRING
    } else if (entry.initials.indexOf(query) === 0) {
      score = SCORE_INITIALS
    } else if (tokens.length > 1 && everyToken(entry.haystack, tokens)) {
      score = SCORE_ALL_TOKENS
    } else if (entry.haystack.indexOf(query) !== -1) {
      score = SCORE_HAYSTACK
    } else {
      const titleFuzzy = query.length >= MIN_TITLE_FUZZY_LENGTH ? fuzzyBonus(title, query) : 0

      if (titleFuzzy > 0) {
        score = SCORE_TITLE_FUZZY + titleFuzzy
      } else {
        const haystackFuzzy = query.length >= MIN_HAYSTACK_FUZZY_LENGTH
          ? fuzzyBonus(entry.haystack, query)
          : 0

        if (haystackFuzzy === 0) {
          return 0
        }

        score = SCORE_HAYSTACK_FUZZY + haystackFuzzy
      }
    }
  }

  // Shorter titles win ties — "Orders" should beat "MVP Bulk Orders" for "ord".
  return score - Math.min(title.length, 48) / 2
}

/**
 * @param {array} entries - Decorated catalog entries
 * @param {string} query - Raw user input
 * @param {object} boosts - path → affinity score, from the usage store
 * @param {number} limit - Maximum results to return
 * @returns {array} Matching entries, best first
 */
export function searchCatalog(entries, query, boosts = {}, limit = 30) {
  const normalized = String(query || '').trim().toLowerCase()

  if (!normalized) {
    return []
  }

  const tokens = normalized.split(/\s+/).filter(Boolean)
  const matches = []
  let hasSolidMatch = false

  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i]
    const base = baseScore(entry, normalized, tokens)

    if (base <= 0) {
      continue
    }

    if (base >= SCORE_TITLE_SUBSTRING) {
      hasSolidMatch = true
    }

    // Pages the user actually lives in float up, and sidebar pages edge out
    // deep ones that were never surfaced in the nav.
    const score = base + (boosts[entry.path] || 0) + (entry.inSidebar ? 8 : 0)
    matches.push({ entry, base, score })
  }

  // Once something genuinely matches the title, stop offering scatter matches
  // alongside it — showing "Tally Export" under a search for "payroll" is what
  // makes a palette feel unreliable. Keyword and section hits still stand.
  const relevant = hasSolidMatch
    ? matches.filter((match) => match.base >= SCORE_HAYSTACK - 48)
    : matches

  relevant.sort((a, b) => b.score - a.score || a.entry.order - b.entry.order)

  return relevant.slice(0, limit).map((match) => match.entry)
}

/**
 * Split a title into `{ text, hit }` runs for match highlighting. Only ever
 * called for the results actually on screen.
 *
 * @param {string} title
 * @param {string} query
 * @returns {Array<{ text: string, hit: boolean }>}
 */
export function highlightTitle(title, query) {
  const normalized = String(query || '').trim().toLowerCase()

  if (!normalized) {
    return [{ text: title, hit: false }]
  }

  const lower = title.toLowerCase()
  const index = lower.indexOf(normalized)

  if (index !== -1) {
    const parts = []
    if (index > 0) parts.push({ text: title.slice(0, index), hit: false })
    parts.push({ text: title.slice(index, index + normalized.length), hit: true })
    if (index + normalized.length < title.length) {
      parts.push({ text: title.slice(index + normalized.length), hit: false })
    }
    return parts
  }

  // Fall back to per-character subsequence highlighting, merging adjacent
  // characters so the markup stays small.
  const parts = []
  let queryIndex = 0
  let buffer = ''
  let bufferHit = false

  for (let i = 0; i < title.length; i++) {
    const hit = queryIndex < normalized.length && lower[i] === normalized[queryIndex]

    if (hit) {
      queryIndex += 1
    }

    if (hit === bufferHit) {
      buffer += title[i]
    } else {
      if (buffer) parts.push({ text: buffer, hit: bufferHit })
      buffer = title[i]
      bufferHit = hit
    }
  }

  if (buffer) {
    parts.push({ text: buffer, hit: bufferHit })
  }

  return parts.length ? parts : [{ text: title, hit: false }]
}
